import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Shield, Users as UsersIcon, Flag, Ban, Check, Loader2, Trash2, UserX, UserCheck, ScrollText, Search, AlertTriangle } from "lucide-react";

const DURATIONS = [
  { id: "1week", label: "1 week" },
  { id: "2weeks", label: "2 weeks" },
  { id: "permanent", label: "Permanent" },
];

const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso || "—"; } };

function StatusBadge({ u }) {
  if (u.deactivated) return <Badge className="bg-muted text-muted-foreground border-0" data-testid={`status-${u.id}`}>Deactivated</Badge>;
  if (u.banned) return <Badge className="bg-destructive/15 text-destructive border-0" data-testid={`status-${u.id}`}>Banned{u.ban_permanent ? " · perm" : ""}</Badge>;
  return <Badge className="bg-secondary/15 text-secondary border-0" data-testid={`status-${u.id}`}>Active</Badge>;
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [banTarget, setBanTarget] = useState(null);
  const [banDuration, setBanDuration] = useState("1week");
  const [banReason, setBanReason] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (search = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users", { params: search ? { q: search } : {} });
      setUsers(data.users);
    } catch (e) { toast.error("Could not load users."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doBan = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/ban/${banTarget.id}`, { duration: banDuration, reason: banReason });
      toast.success(`${banTarget.username} banned (${banDuration}).`);
      setBanTarget(null); setBanReason(""); setBanDuration("1week");
      load(q);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doAction = async (fn, id, msg) => {
    try { await fn(id); toast.success(msg); load(q); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/user/${deleteTarget.id}`);
      toast.success(`${deleteTarget.username} permanently deleted.`);
      setDeleteTarget(null);
      load(q);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-6">
      <form onSubmit={(e) => { e.preventDefault(); load(q); }} className="flex gap-2 mb-4 max-w-md">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search username, email or name…" className="pl-9 rounded-full bg-accent/50 border-transparent" data-testid="admin-user-search" />
        </div>
        <Button type="submit" variant="outline" className="rounded-full" data-testid="admin-user-search-btn">Search</Button>
      </form>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : (
        <div className="rounded-3xl border border-border overflow-hidden" data-testid="admin-users-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Points</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} data-testid={`admin-user-row-${u.id}`}>
                  <TableCell>
                    <div className="font-heading font-bold">@{u.username || "—"}</div>
                    <div className="text-xs text-muted-foreground">{u.email}</div>
                    {u.banned && u.ban_reason && <div className="text-[11px] text-destructive mt-0.5">Reason: {u.ban_reason}{!u.ban_permanent && u.ban_until ? ` · until ${fmt(u.ban_until)}` : ""}</div>}
                  </TableCell>
                  <TableCell>
                    {u.role === "admin"
                      ? <Badge className="bg-primary/15 text-primary border-0">admin</Badge>
                      : <span className="text-sm text-muted-foreground">user</span>}
                  </TableCell>
                  <TableCell className="text-sm">{u.points} · {u.tier}</TableCell>
                  <TableCell><StatusBadge u={u} /></TableCell>
                  <TableCell className="text-right">
                    {u.role === "admin" ? (
                      <span className="text-xs text-muted-foreground italic">protected</span>
                    ) : (
                      <div className="flex items-center justify-end gap-1.5 flex-wrap">
                        {u.banned ? (
                          <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => doAction((id) => api.post(`/admin/unban/${id}`), u.id, `${u.username} unbanned.`)} data-testid={`unban-${u.id}`}>
                            <UserCheck className="w-3.5 h-3.5 mr-1" /> Unban
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" className="rounded-full h-8 border-destructive/40 text-destructive hover:bg-destructive/10" onClick={() => setBanTarget(u)} data-testid={`ban-${u.id}`}>
                            <Ban className="w-3.5 h-3.5 mr-1" /> Ban
                          </Button>
                        )}
                        {u.deactivated ? (
                          <Button size="sm" variant="ghost" className="rounded-full h-8" onClick={() => doAction((id) => api.post(`/admin/reactivate/${id}`), u.id, `${u.username} reactivated.`)} data-testid={`reactivate-${u.id}`}>
                            <UserCheck className="w-3.5 h-3.5 mr-1" /> Reactivate
                          </Button>
                        ) : (
                          <Button size="sm" variant="ghost" className="rounded-full h-8" onClick={() => doAction((id) => api.post(`/admin/deactivate/${id}`, { reason: "" }), u.id, `${u.username} deactivated.`)} data-testid={`deactivate-${u.id}`}>
                            <UserX className="w-3.5 h-3.5 mr-1" /> Deactivate
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="rounded-full h-8 text-destructive hover:bg-destructive/10" onClick={() => setDeleteTarget(u)} data-testid={`delete-${u.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Ban dialog */}
      <Dialog open={!!banTarget} onOpenChange={(o) => !o && setBanTarget(null)}>
        <DialogContent className="rounded-3xl" data-testid="ban-dialog">
          <DialogHeader><DialogTitle className="font-heading">Ban @{banTarget?.username}</DialogTitle></DialogHeader>
          <p className="text-sm text-foreground/70 -mt-2">The user won't be able to log in or make calls until the ban expires.</p>
          <div>
            <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-2">Duration</p>
            <div className="flex gap-2">
              {DURATIONS.map((d) => (
                <button key={d.id} type="button" onClick={() => setBanDuration(d.id)} data-testid={`ban-duration-${d.id}`}
                  className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all ${banDuration === d.id ? "bg-destructive text-white border-destructive" : "border-border text-foreground/70 hover:border-destructive/40"}`}>
                  {d.label}
                </button>
              ))}
            </div>
          </div>
          <Textarea value={banReason} onChange={(e) => setBanReason(e.target.value)} rows={3} placeholder="Reason (recorded in the activity log)…" className="rounded-2xl bg-accent/50 border-transparent mt-2" data-testid="ban-reason" />
          <DialogFooter>
            <Button variant="ghost" className="rounded-full" onClick={() => setBanTarget(null)}>Cancel</Button>
            <Button className="rounded-full bg-destructive hover:bg-destructive/90" onClick={doBan} disabled={busy} data-testid="ban-confirm">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ban user"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-3xl" data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-destructive" /> Delete @{deleteTarget?.username}?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the account and their data. This cannot be undone. To reverse instead, use Deactivate.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction className="rounded-full bg-destructive hover:bg-destructive/90" onClick={doDelete} data-testid="delete-confirm">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const sevColor = { high: "bg-destructive/15 text-destructive", medium: "bg-secondary/15 text-secondary", low: "bg-muted text-muted-foreground" };

function ReportsTab() {
  const [data, setData] = useState({ cases: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const load = () => { setLoading(true); api.get("/moderation/reports").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  const resolve = async (id) => { await api.post(`/moderation/resolve/${id}`).catch(() => {}); toast.success("Case marked resolved"); load(); };
  const stat = (label, value, Icon) => (
    <div className="rounded-3xl bg-card border border-border p-5 flex items-center gap-4">
      <span className="w-11 h-11 rounded-2xl bg-primary/10 text-primary flex items-center justify-center"><Icon className="w-5 h-5" /></span>
      <div><p className="text-2xl font-heading font-black">{value ?? 0}</p><p className="text-xs uppercase tracking-widest text-muted-foreground font-bold">{label}</p></div>
    </div>
  );
  return (
    <div className="mt-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stat("Total cases", data.stats.total, Flag)}
        {stat("Open", data.stats.open, AlertTriangle)}
        {stat("Flagged", data.stats.flagged, Shield)}
        {stat("High severity", data.stats.high, Ban)}
      </div>
      <div className="mt-8 space-y-4">
        {loading ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          : data.cases.length === 0 ? <p className="text-muted-foreground" data-testid="no-cases">No reports yet. All quiet 🎉</p>
          : data.cases.map((c) => (
            <div key={c.id} className="rounded-3xl bg-card border border-border p-6" data-testid={`case-${c.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <Badge className={c.kind === "block" ? "bg-destructive/15 text-destructive border-0" : "bg-primary/15 text-primary border-0"}>
                  {c.kind === "block" ? <Ban className="w-3 h-3 mr-1" /> : <Flag className="w-3 h-3 mr-1" />}{c.kind}
                </Badge>
                {c.ai_flagged && <Badge className="bg-destructive/15 text-destructive border-0">Flagged: {c.ai_category}</Badge>}
                <Badge className={`border-0 ${sevColor[c.ai_severity] || sevColor.low}`}>{c.ai_severity} severity</Badge>
                <Badge className="bg-muted text-muted-foreground border-0">action: {c.ai_action}</Badge>
                {c.status === "resolved" && <Badge className="bg-secondary/15 text-secondary border-0"><Check className="w-3 h-3 mr-1" />resolved</Badge>}
                <span className="ml-auto text-xs text-muted-foreground">{fmt(c.created_at)}</span>
              </div>
              <p className="mt-3 text-sm"><span className="font-semibold">{c.reporter_name}</span> reported <span className="font-semibold">{c.reported_name}</span></p>
              {c.content && <p className="mt-2 text-sm bg-muted rounded-2xl p-3 text-foreground/80">“{c.content}”</p>}
              <p className="mt-3 text-sm text-foreground/70"><span className="font-semibold text-foreground">Review note:</span> {c.ai_reason}</p>
              {c.status !== "resolved" && <Button size="sm" variant="outline" className="rounded-full mt-4" onClick={() => resolve(c.id)} data-testid={`resolve-${c.id}`}><Check className="w-4 h-4 mr-1" /> Mark resolved</Button>}
            </div>
          ))}
      </div>
    </div>
  );
}

const ACTION_STYLE = {
  ban: "bg-destructive/15 text-destructive", unban: "bg-secondary/15 text-secondary",
  deactivate: "bg-muted text-muted-foreground", reactivate: "bg-secondary/15 text-secondary",
  delete: "bg-destructive/15 text-destructive",
};
const ACTION_PAST = { ban: "banned", unban: "unbanned", deactivate: "deactivated", reactivate: "reactivated", delete: "deleted" };

function ActivityTab() {
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/admin/activity-log").then((r) => setLog(r.data.log)).catch(() => {}).finally(() => setLoading(false)); }, []);
  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  if (!log.length) return <p className="text-muted-foreground mt-6" data-testid="no-activity">No admin activity yet.</p>;
  return (
    <div className="mt-6 space-y-3" data-testid="activity-log">
      {log.map((e) => (
        <div key={e.id} className="rounded-2xl bg-card border border-border p-4 flex flex-wrap items-center gap-3" data-testid={`activity-${e.id}`}>
          <Badge className={`border-0 capitalize ${ACTION_STYLE[e.action] || "bg-muted text-muted-foreground"}`}>{e.action}</Badge>
          <span className="text-sm"><span className="font-semibold">{e.admin_name}</span> {ACTION_PAST[e.action] || e.action} <span className="font-semibold">{e.target_name || "—"}</span>
            {e.meta?.duration ? <span className="text-muted-foreground"> ({e.meta.duration})</span> : null}</span>
          {e.reason && <span className="text-sm text-foreground/70">— “{e.reason}”</span>}
          <span className="ml-auto text-xs text-muted-foreground">{fmt(e.created_at)}</span>
        </div>
      ))}
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  if (user && user.role !== "admin") return <Navigate to="/app" replace />;
  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <h1 className="text-3xl sm:text-4xl font-heading font-black tracking-tight flex items-center gap-3">
          <Shield className="w-8 h-8 text-primary" /> Admin Dashboard
        </h1>
        <p className="text-foreground/70 mt-1">Manage users, review reports, and audit moderation activity.</p>

        <Tabs defaultValue="users" className="mt-8">
          <TabsList className="rounded-full">
            <TabsTrigger value="users" className="rounded-full" data-testid="tab-users"><UsersIcon className="w-4 h-4 mr-1.5" /> Users</TabsTrigger>
            <TabsTrigger value="reports" className="rounded-full" data-testid="tab-reports"><Flag className="w-4 h-4 mr-1.5" /> Reports</TabsTrigger>
            <TabsTrigger value="activity" className="rounded-full" data-testid="tab-activity"><ScrollText className="w-4 h-4 mr-1.5" /> Activity Log</TabsTrigger>
          </TabsList>
          <TabsContent value="users"><UsersTab /></TabsContent>
          <TabsContent value="reports"><ReportsTab /></TabsContent>
          <TabsContent value="activity"><ActivityTab /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
